import axios from 'axios';

const API_BASE_URL = '/api';

/**
 * Fetches the list of pre-built decks from the server.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of deck objects.
 * @throws Will throw an error if the fetch fails or the response is not ok.
 */
export const fetchDecks = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/decks`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const decks = await response.json();
    return decks;
  } catch (error) {
    console.error("Failed to fetch decks:", error);
    // Re-throw the error so calling components can handle it
    throw error;
  }
};

/**
 * Fetches the assets (images, etc.) for a specific deck.
 * @param {string} deckId - The ID of the deck to fetch assets for
 * @returns {Promise<object>} A promise that resolves to an object containing deck assets
 * @throws Will throw an error if the fetch fails or the response is not ok
 */
export const fetchDeckAssets = async (deckId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/decks/${deckId}/assets`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const assets = await response.json();
    return assets;
  } catch (error) {
    console.error(`Failed to fetch assets for deck ${deckId}:`, error);
    throw error;
  }
};


export const fetchRooms = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/rooms`);
    const data = await response.json();
    return data.rooms;
  } catch (error) {
    console.error("Failed to fetch rooms:", error);
    throw error;
  }
};

export const getUserData = async () => {
  const response = await axios.get('/api/me');
  return response.data;
};

export const getConfig = async () => {
  const response = await axios.get('/api/config');
  return response.data;
};

export const saveConfig = async (key, value) => {
  const response = await axios.post('/api/config', { key, value });
  return response.data;
};

export const getRecentGames = async () => {
  const response = await axios.get('/api/recent-games');
  return response.data;
};

export const getDauAnalytics = async () => {
  const response = await axios.get('/api/analytics/dau');
  return response.data;
};

export const startBot = async (deckId = 'ST01') => {
  const response = await axios.post('/api/start-bot', { deckId });
  return response.data;
};

export const fetchCollection = async () => {
  const response = await axios.get('/api/collection');
  return response.data;
};

export const fetchFullCollection = async () => {
  const response = await axios.get('/api/collection/full');
  return response.data;
};

export const fetchShopItems = async () => {
  const response = await axios.get('/api/shop/items');
  return response.data;
};

export const claimShopItem = async (itemId) => {
  const response = await axios.post('/api/shop/claim', { itemId });
  return response.data;
};

export const fetchWallet = async () => {
  const response = await axios.get('/api/wallet');
  return response.data;
};

export const adminGrantCards = async (userId, cards) => {
  const response = await axios.post('/api/collection/grant', { userId, cards });
  return response.data;
};

export const adminClearCollection = async (userId) => {
  const response = await axios.post('/api/collection/clear', { userId });
  return response.data;
};

export const adminGrantCoins = async (userId, amount) => {
  const response = await axios.post('/api/wallet/grant', { userId, amount });
  return response.data;
};

export const adminListPlayers = async () => {
  const response = await axios.get('/api/admin/players');
  return response.data;
};

export const adminLoginAsNewUser = async () => {
  const response = await axios.post('/api/admin/login-as-new-user');
  return response.data;
};

export const adminWipePlayerData = async (confirmation) => {
  const response = await axios.post('/api/admin/wipe-player-data', { confirmation });
  return response.data;
};

export const createDeck = async (name, leader, cards) => {
  const response = await axios.post('/api/decks', { name, leader, cards });
  return response.data;
};

export const updateDeck = async (deckId, name, leader, cards) => {
  const response = await axios.put(`/api/decks/${deckId}`, { name, leader, cards });
  return response.data;
};

export const deleteDeck = async (deckId) => {
  const response = await axios.delete(`/api/decks/${deckId}`);
  return response.data;
};

/** @returns {Promise<{ source: 'db'|'code'|'none', effect: Array<object> }>} */
export const getCardEffect = async (cardId) => {
  const response = await axios.get(`/api/effects/cards/${cardId}`);
  return response.data;
};

/** @returns {Promise<object>} Saved CardEffect document. */
export const saveCardEffect = async (cardId, effect) => {
  const response = await axios.post(`/api/effects/cards/${cardId}`, { effect });
  return response.data;
};

/** @returns {Promise<object>} Delete result (removes all DB versions for the card). */
export const deleteCardEffect = async (cardId) => {
  const response = await axios.delete(`/api/effects/cards/${cardId}`);
  return response.data;
};

/** @returns {Promise<string[]>} Card IDs that have effectText but no JSON file implementation. */
export const fetchUnimplementedEffectCardIds = async () => {
  const response = await axios.get('/api/effects/not-implemented');
  return response.data;
};